// Composant pour afficher et gérer le formulaire de création/édition d'un type de licence
import {
  XMarkIcon,
  CheckIcon,
  CurrencyEuroIcon,
  TagIcon,
  DocumentTextIcon,
  CalendarIcon,
  ComputerDesktopIcon,
  CogIcon
} from '@heroicons/react/24/outline';
import { LicenseTypeFormData } from './useLicenseTypeForm';

interface LicenseTypeFormProps {
  formData: LicenseTypeFormData;
  setFormData: (data: LicenseTypeFormData) => void;
  isCreating: boolean;
  saving: boolean;
  onSave: () => void;
  onCancel: () => void;
}

export function LicenseTypeForm({
  formData,
  setFormData,
  isCreating,
  saving,
  onSave,
  onCancel
}: LicenseTypeFormProps) {
  return (
    <div className="bg-white/10 dark:bg-gray-900/10 backdrop-blur-lg rounded-xl border border-white/20 p-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          {isCreating ? 'Créer un nouveau type de licence' : 'Modifier le type de licence'}
        </h3>
        <button
          onClick={onCancel}
          className="p-2 hover:bg-gray-200/50 dark:hover:bg-gray-700/50 rounded-lg transition-colors"
        >
          <XMarkIcon className="h-5 w-5 text-gray-500" />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {/* Nom */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            <TagIcon className="h-4 w-4 inline mr-1" />
            Nom *
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full bg-white/20 dark:bg-gray-900/20 border border-white/30 rounded-lg px-3 py-2 text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            placeholder="Ex: Licence Premium"
          />
        </div>

        {/* Prix */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            <CurrencyEuroIcon className="h-4 w-4 inline mr-1" />
            Prix (€) *
          </label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={formData.price}
            onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
            className="w-full bg-white/20 dark:bg-gray-900/20 border border-white/30 rounded-lg px-3 py-2 text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            placeholder="299.99"
          />
        </div>

        {/* Durée en jours */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            <CalendarIcon className="h-4 w-4 inline mr-1" />
            Durée (jours)
          </label>
          <input
            type="number"
            min="0"
            value={formData.duration_days || ''}
            onChange={(e) => setFormData({
              ...formData,
              duration_days: e.target.value ? parseInt(e.target.value) : undefined
            })}
            className="w-full bg-white/20 dark:bg-gray-900/20 border border-white/30 rounded-lg px-3 py-2 text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            placeholder="365 (vide = à vie)"
          />
        </div>

        {/* Nombre max d'appareils */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            <ComputerDesktopIcon className="h-4 w-4 inline mr-1" />
            Appareils max
          </label>
          <input
            type="number"
            min="1"
            value={formData.max_devices}
            onChange={(e) => setFormData({ ...formData, max_devices: parseInt(e.target.value) || 1 })}
            className="w-full bg-white/20 dark:bg-gray-900/20 border border-white/30 rounded-lg px-3 py-2 text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Description */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          <DocumentTextIcon className="h-4 w-4 inline mr-1" />
          Description
        </label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          rows={3}
          className="w-full bg-white/20 dark:bg-gray-900/20 border border-white/30 rounded-lg px-3 py-2 text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          placeholder="Description du type de licence..."
        />
      </div>

      {/* Fonctionnalités */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          <CogIcon className="h-4 w-4 inline mr-1" />
          Fonctionnalités (JSON)
        </label>
        <textarea
          value={JSON.stringify(formData.features, null, 2)}
          onChange={(e) => {
            try {
              const features = JSON.parse(e.target.value);
              setFormData({ ...formData, features });
            } catch {
              // Ignore les erreurs de parsing pendant la saisie
            }
          }}
          rows={4}
          className="w-full bg-white/20 dark:bg-gray-900/20 border border-white/30 rounded-lg px-3 py-2 text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono text-sm"
          placeholder='{"features": ["full_access", "premium_support"]}'
        />
      </div>

      {/* Statut actif */}
      <div className="flex items-center gap-2 mb-6">
        <input
          type="checkbox"
          id="is_active"
          checked={formData.is_active}
          onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
          className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
        />
        <label htmlFor="is_active" className="text-sm text-gray-700 dark:text-gray-300">
          Type de licence actif
        </label>
      </div>

      {/* Boutons d'action */}
      <div className="flex gap-2 justify-end">
        <button
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          Annuler
        </button>
        <button
          onClick={onSave}
          disabled={saving}
          className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg transition-colors"
        >
          {saving ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          ) : (
            <CheckIcon className="h-4 w-4" />
          )}
          {isCreating ? 'Créer' : 'Sauvegarder'}
        </button>
      </div>
    </div>
  );
}
